import React from 'react';
import {
  createUnsavedChangesRegistry,
  UnsavedChangesRegistry,
} from './unsavedChangesRegistry';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { useConfirmTarget } from './useConfirmTarget';

interface UnsavedChangesContextValue {
  registry: UnsavedChangesRegistry;
  /**
   * Runs `run` immediately and returns true when nothing is dirty.
   * When something is dirty, opens the discard dialog, returns false,
   * and runs `run` only if the user confirms discarding.
   */
  guardNavigation: (run: () => void) => boolean;
}

const fallbackValue: UnsavedChangesContextValue = {
  registry: createUnsavedChangesRegistry(),
  guardNavigation: (run) => {
    run();
    return true;
  },
};

const UnsavedChangesContext = React.createContext<UnsavedChangesContextValue>(fallbackValue);

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const registryRef = React.useRef<UnsavedChangesRegistry>();
  if (!registryRef.current) {
    registryRef.current = createUnsavedChangesRegistry();
  }
  const registry = registryRef.current;

  const t = useConfirmTarget<{ run: () => void }>();
  const [pendingLabels, setPendingLabels] = React.useState<string[]>([]);

  const guardNavigation = React.useCallback(
    (run: () => void) => {
      if (!registry.hasDirty()) {
        run();
        return true;
      }
      setPendingLabels(registry.dirtyLabels());
      t.set({ run });
      return false;
    },
    [registry, t],
  );

  const value = React.useMemo(
    () => ({ registry, guardNavigation }),
    [registry, guardNavigation],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <ConfirmActionDialog
        open={t.open}
        onOpenChange={(open) => {
          if (!open) t.clear();
        }}
        title="Discard unsaved changes?"
        description={
          pendingLabels.length > 0
            ? `You have unsaved edits in: ${pendingLabels.join(', ')}.`
            : 'You have unsaved edits.'
        }
        confirmLabel="Discard changes"
        tone="destructive"
        onConfirm={() => {
          if (!t.target) return;
          t.target.run();
        }}
      />
    </UnsavedChangesContext.Provider>
  );
};

export function useUnsavedChanges(): UnsavedChangesContextValue {
  return React.useContext(UnsavedChangesContext);
}

export function useUnsavedChangesGuard(isDirty: boolean, label: string): void {
  const id = React.useId();
  const { registry } = useUnsavedChanges();

  React.useEffect(() => {
    if (!isDirty) {
      registry.unregister(id);
      return undefined;
    }
    registry.register(id, label);
    return () => registry.unregister(id);
  }, [isDirty, label, id, registry]);

  React.useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);
}
